import {
  $createTableNodeWithDimensions,
  $createTableRowNode,
  $createTableCellNode,
  $deleteTableColumn,
  $isTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowNodeFromTableCellNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $getTableColumnIndexFromTableCellNode,
} from '@lexical/table';
import { type TextNode, type ElementNode, $createParagraphNode } from 'lexical';

import type { TableCellNode } from '@lexical/table';

const PERCENTAGE = 100;

export const createTable = (nrOfRows: number, nrOfColumns: number): ElementNode => {
  const tableNode = $createTableNodeWithDimensions(0, 0, false);
  for (let rowIndex = 0; rowIndex < nrOfRows; rowIndex++) {
    const rowNode = $createTableRowNode();
    for (let colIndex = 0; colIndex < nrOfColumns; colIndex++) {
      const cellNode = $createTableCellNode(0, undefined, PERCENTAGE / nrOfColumns);
      cellNode.append($createParagraphNode());
      rowNode.append(cellNode);
    }
    tableNode.append(rowNode);
  }
  return tableNode;
};

export const deleteRow = (selectedNode: ElementNode) => {
  const currentRow = $getTableRowNodeFromTableCellNodeOrThrow(selectedNode);
  const nextRow = currentRow.getNextSibling() || currentRow.getPreviousSibling();
  const currentTable = currentRow.getParentOrThrow();
  currentTable.getChildrenSize() > 1 ? currentRow.remove() : currentTable.remove();

  if (nextRow !== null) {
    const firstDescendant = (nextRow as ElementNode).getFirstDescendant();
    if (firstDescendant) {
      (firstDescendant as TextNode).select();
    }
  }
};

export const deleteTable = (selectedNode: ElementNode) => {
  $getTableNodeFromLexicalNodeOrThrow(selectedNode).remove();
};

export const insertRow = (selectedNode: ElementNode, position: 'insertRowAbove' | 'insertRowBelow') => {
  const newRow = $createTableRowNode();
  const currentRow = $getTableRowNodeFromTableCellNodeOrThrow(selectedNode);
  const nrOfCols = currentRow.getChildrenSize();
  for (let rowCount = 0; rowCount < nrOfCols; rowCount++) {
    const newCell = $createTableCellNode(0);
    newCell.append($createParagraphNode());
    newRow.append(newCell);
  }
  position === 'insertRowAbove' ? currentRow.insertBefore(newRow) : currentRow.insertAfter(newRow);
  const firstDescendant = newRow.getFirstDescendant();
  if (firstDescendant) {
    (firstDescendant as TextNode).select();
  }
};

export const insertColumn = (selectedNode: ElementNode, position: 'insertColumnLeft' | 'insertColumnRight') => {
  const currentRow = $getTableRowNodeFromTableCellNodeOrThrow(selectedNode);
  if (!$isTableCellNode(selectedNode)) {
    throw new Error('Selected node is not a TableCellNode');
  }
  const rowIndex = $getTableRowIndexFromTableCellNode(selectedNode);
  const nrOfCols = currentRow.getChildren().length;
  const currentTable = $getTableNodeFromLexicalNodeOrThrow(selectedNode);
  let columnIndex = $getTableColumnIndexFromTableCellNode(selectedNode);
  const rowsInTable: TableCellNode[] = currentTable.getChildren();
  const nrOfRows = rowsInTable.length;

  for (let rowCount = 0; rowCount < nrOfRows; rowCount++) {
    const newCell = $createTableCellNode(0);
    newCell.append($createParagraphNode());
    const cellInSelectedColumn = rowsInTable[rowCount].getChildren()[columnIndex];
    if (cellInSelectedColumn) {
      if (position === 'insertColumnLeft') {
        cellInSelectedColumn.insertBefore(newCell);
      } else {
        cellInSelectedColumn.insertAfter(newCell);
      }
    }

    rowsInTable.forEach((row) => {
      row.getChildren().forEach((cell) => {
        (cell as TableCellNode).setWidth(PERCENTAGE / nrOfCols);
      });
    });
  }

  columnIndex = position === 'insertColumnLeft' ? columnIndex : columnIndex + 1;
  const firstDescendant = rowsInTable[rowIndex].getChildren()[columnIndex];
  if (firstDescendant) {
    (firstDescendant as TextNode).select();
  }
};

export const deleteColumn = (selectedNode: ElementNode) => {
  if (!$isTableCellNode(selectedNode)) {
    throw new Error('Selected node is not a TableCellNode');
  }
  const currentTable = $getTableNodeFromLexicalNodeOrThrow(selectedNode);
  const numberOfColumns = (currentTable.getFirstChild() as ElementNode)?.getChildrenSize() || 0;
  const columnIndex = $getTableColumnIndexFromTableCellNode(selectedNode);

  if (numberOfColumns === 1) {
    deleteTable(selectedNode);
  } else {
    $deleteTableColumn(currentTable, columnIndex);
  }
};
